import { FarePriceErrorResponse, FarePriceRequest, FarePriceResponse } from "@ssc-booking/api-contracts"
import { useActor } from "@xstate/react"
import { match } from "ts-pattern"
import {
    dataLoadMachine,
    dataLoadTypes,
    RequestResponseChannel,
} from "../../../../../_common/machines/data-load-machine"
import { ECommerceEvent, ECommerceState } from "../e-commerce-machine"
import { FareCardIndex } from "../../../read-models/fare-card"

const _types = dataLoadTypes(FarePriceRequest, FarePriceResponse, FarePriceErrorResponse)
type FarePriceLoaderTypes = typeof _types
export type FarePriceLoaderState = FarePriceLoaderTypes["State"]
type FarePriceLoaderSend = FarePriceLoaderTypes["Send"]
export type FarePriceLoaderActorRef = FarePriceLoaderTypes["ActorRef"]

const notifyParent =
    (index: FareCardIndex) =>
    (ctx: FarePriceLoaderTypes["Context"], ev: FarePriceLoaderTypes["Events"]): ECommerceEvent | undefined =>
        match<FarePriceLoaderTypes["Events"], ECommerceEvent | undefined>(ev)
            .with({ type: "LOAD" }, () => ({ type: "FARE_PRICE_LOADING", index }))
            .with({ type: "LOAD_SUCCESS" }, (ev) => ({ type: "FARE_PRICE_LOADED", index, response: ev.response }))
            .with({ type: "LOAD_ERROR" }, (ev) => ({ type: "FARE_PRICE_ERROR", index, code: ev.response.errorCode }))
            .with({ type: "LOAD_FATAL_ERROR" }, () => ({ type: "FARE_PRICE_ERROR", index, code: "fatal" }))
            .otherwise(() => undefined)

export const farePriceLoaderId = (index: number): string => `farePriceLoader_${index}`

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const buildFarePriceLoader = (websocketClient: RequestResponseChannel, index: FareCardIndex) =>
    dataLoadMachine({
        id: farePriceLoaderId(index),
        channel: websocketClient,
        Request: FarePriceRequest,
        Success: FarePriceResponse,
        successType: "PriceResponseV2",
        Error: FarePriceErrorResponse,
        errorType: "FarePriceErrorResponse",
        action: "fare-price",
        notifyParent: notifyParent(index),
    })

export const useFarePriceLoader = (
    state: ECommerceState,
    index: FareCardIndex,
): [FarePriceLoaderState, FarePriceLoaderSend] => {
    const farePriceLoader = state.children[farePriceLoaderId(index)] as FarePriceLoaderActorRef
    return useActor(farePriceLoader) as unknown as [FarePriceLoaderState, FarePriceLoaderSend]
}
